import { Client, isRunningEntry, TimeEntry } from "../store/timeTrackerStore";
import {
  getCompletedWeekRange,
  getCurrentDayRange,
  getCurrentMonthRange,
  getCurrentWeekRange,
} from "./analyticsPeriods";

// Orders clients for the stop-timer "What did you work on?" picker only.
// Clients with an explicit pickerOrder come first, ascending; clients without
// one fall to the bottom. Within each group, ties break alphabetically so the
// order is always total and stable. Pure and non-mutating — callers pass the
// active-client list and render the result.
export function sortClientsForPicker(clients: Client[]): Client[] {
  return [...clients].sort((a, b) => {
    const ao = a.pickerOrder ?? Number.POSITIVE_INFINITY;
    const bo = b.pickerOrder ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
}

export type DatePreset =
  | "today"
  | "week"
  | "lastWeek"
  | "30days"
  | "thisMonth"
  | "last4Weeks"
  | "all";

// Order is the chip order in the entries-list filter UI.
export const DATE_PRESETS: DatePreset[] = [
  "today",
  "week",
  "lastWeek",
  "30days",
  "thisMonth",
  "last4Weeks",
  "all",
];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: "Today",
  week: "This week",
  lastWeek: "Last week",
  "30days": "Last 30 days",
  thisMonth: "This month",
  last4Weeks: "Last 4 weeks",
  all: "All",
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// Filter entries to those whose startTime falls within the preset window.
// A running entry (no endTime) is always retained — the user must be able to
// see and stop it regardless of which window is in view.
export function filterEntriesByDatePreset(
  entries: TimeEntry[],
  preset: DatePreset,
  now: Date = new Date(),
): TimeEntry[] {
  if (preset === "all") return entries;

  // `lastWeek` is the only closed window — bounded above by Monday of *this*
  // week. The others run from a fixed point up to now.
  if (preset === "lastWeek") {
    const r = getCompletedWeekRange(now, 1);
    const startMs = r.start.getTime();
    const endMs = r.end.getTime();
    return entries.filter(
      (e) =>
        e.endTime === undefined ||
        (e.startTime >= startMs && e.startTime < endMs),
    );
  }

  let startMs: number;
  if (preset === "today") {
    startMs = getCurrentDayRange(now).start.getTime();
  } else if (preset === "week") {
    startMs = getCurrentWeekRange(now).start.getTime();
  } else if (preset === "thisMonth") {
    // The current calendar month, 1st at 00:00 up to now — matches the billing
    // "This month" column so clicking that cell lands on a reconciling list,
    // not the rolling 30-day window `30days` gives.
    startMs = getCurrentMonthRange(now).start.getTime();
  } else if (preset === "last4Weeks") {
    // Monday of three full weeks back — together with the in-progress current
    // week, that's 4 calendar weeks ending now.
    startMs = getCompletedWeekRange(now, 3).start.getTime();
  } else {
    startMs = now.getTime() - THIRTY_DAYS_MS;
  }
  return entries.filter((e) => e.endTime === undefined || e.startTime >= startMs);
}

export interface DayGroup {
  dayKey: string;
  dayStartMs: number;
  totalMs: number;
  entries: TimeEntry[];
}

// Group entries by local-calendar day, preserving the input order within each
// day. Running entries (no endTime) contribute their live elapsed time so the
// day total stays consistent with the per-row "running" duration the user sees.
export function groupEntriesByDay(
  entries: TimeEntry[],
  now: number = Date.now(),
): DayGroup[] {
  const groups: DayGroup[] = [];
  const seen = new Map<string, number>();
  for (const entry of entries) {
    const d = new Date(entry.startTime);
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const duration =
      entry.endTime !== undefined
        ? entry.endTime - entry.startTime
        : Math.max(0, now - entry.startTime);
    const idx = seen.get(dayKey);
    if (idx !== undefined) {
      groups[idx].totalMs += duration;
      groups[idx].entries.push(entry);
    } else {
      const dayStart = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
      ).getTime();
      seen.set(dayKey, groups.length);
      groups.push({
        dayKey,
        dayStartMs: dayStart,
        totalMs: duration,
        entries: [entry],
      });
    }
  }
  return groups;
}

/**
 * Verify the invariant that only a single time entry can be running at once
 * Throws an error if multiple running entries are found
 */
export function validateSingleRunningEntry(entries: TimeEntry[]) {
  const runningEntries = entries.filter(isRunningEntry);
  if (runningEntries.length > 1) {
    throw new Error(
      `Invariant violation: Found ${runningEntries.length} running time entries. Only one entry can be running at a time.`
    );
  }
}

/**
 * Convert Unix time duration (in milliseconds) to human readable string
 * @param {number} milliseconds - Duration in milliseconds
 * @param {Object} options - Formatting options
 * @param {boolean} options.short - Use short form (e.g. "2d 5h" instead of "2 days 5 hours")
 * @param {boolean} options.round - Round to most significant unit
 * @param {number} options.maxUnits - Maximum number of units to display (default: 2)
 * @returns {string} Human readable duration string
 */
export function formatDuration(
  milliseconds: number,
  options: { short?: boolean; round?: boolean; maxUnits?: number } = {}
) {
  // Handle edge cases
  if (milliseconds === 0) return options.short ? "0s" : "0 seconds";
  if (!Number.isFinite(milliseconds) || isNaN(milliseconds))
    return "Invalid duration";

  const seconds = milliseconds / 1000;

  // Default options
  const short = options.short || false;
  const round = options.round || false;
  const maxUnits = options.maxUnits || 2;

  // Define time units in seconds
  const units = [
    { name: "year", short: "y", seconds: 31536000 },
    { name: "month", short: "mo", seconds: 2592000 },
    { name: "week", short: "w", seconds: 604800 },
    { name: "day", short: "d", seconds: 86400 },
    { name: "hour", short: "h", seconds: 3600 },
    { name: "minute", short: "m", seconds: 60 },
    { name: "second", short: "s", seconds: 1 },
  ];

  // If rounding to significant unit
  if (round) {
    for (const unit of units) {
      if (seconds >= unit.seconds) {
        const value = Math.round(seconds / unit.seconds);
        const name = value === 1 ? unit.name : unit.name + "s";
        return short ? `${value}${unit.short}` : `${value} ${name}`;
      }
    }
  }

  let remainingSeconds = Math.abs(seconds);
  const parts = [];

  // Break down seconds into different units
  for (const unit of units) {
    if (remainingSeconds >= unit.seconds) {
      const value = Math.floor(remainingSeconds / unit.seconds);
      remainingSeconds %= unit.seconds;

      if (short) {
        parts.push(`${value}${unit.short}`);
      } else {
        const name = value === 1 ? unit.name : unit.name + "s";
        parts.push(`${value} ${name}`);
      }

      // Stop if we've reached maximum number of units
      if (parts.length >= maxUnits) break;
    }
  }

  // If no parts, it's less than 1 second
  if (parts.length === 0) {
    return short ? "< 1s" : "less than 1 second";
  }

  // Join the parts with appropriate separator
  return short ? parts.join(" ") : parts.join(", ");
}
